/*
 * Copyright (C) 2026 - present Marek Kuzora - All Rights Reserved.
 */


import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons';
import { Card, Flex, IconButton, Inset } from '@radix-ui/themes';

import { styled, use, useCallback } from 'fierry/react';

import Text from 'solis/react/primitive/text';

import Navigation from 'solis/navigation';

import Args from 'solis/claude/action/args';

import Picker from 'solis/claude/primitive/picker';

import { getSourceCandidates } from 'solis/claude/action/source.colony';

import ColonyInfluence from 'solis/claude/element/colony.influence';


const Header = styled.div`
  display: flex;
  align-items: stretch;
  height: 44px;
  border-bottom: 1px solid var(--gray-a4);
`;


const Title = styled.button`
  flex: 1;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  padding: 0 var(--card-padding);
  border: none;
  background: none;

  color: var(--gray-12);
  font: inherit;
  font-size: var(--font-size-2);
  letter-spacing: 0.02em;
  cursor: pointer;

  &:hover { background: var(--gray-3); }
  &:active { background: var(--gray-4); }

  &:disabled { cursor: default; }

  &:disabled:hover { background: none; }
`;


const Arrow = styled(IconButton)`
  width: 40px;
  height: auto;
  margin: 0;
  border-radius: 0;
`;


const OnChange = (value, dispatch) =>
{
  dispatch.replaceArgs({ [Args.SourceColony]: value });
};


export default ({ context, influenceDelta }) =>
{
  const dispatch = use(Navigation.Dispatch);

  const onChange = useCallback(OnChange, [ dispatch ]);

  const { board, account, sourceColony } = context;

  const candidates = getSourceCandidates(board, account);

  if (sourceColony === null)
  {
    return null;
  }

  const idx = candidates.findIndex(colony => colony.cass.id === sourceColony.cass.id);

  const onPrev = () =>
  {
    onChange(candidates[(idx - 1 + candidates.length) % candidates.length].cass.id);
  };

  const onNext = () =>
  {
    onChange(candidates[(idx + 1) % candidates.length].cass.id);
  };

  const options = candidates.map(colony => ({ value: colony.cass.id, label: colony.getName() }));

  const title =
  (
    <Title type = 'button' disabled = {candidates.length <= 1} aria-label = 'Choose source colony'>
      <Text color = 'gray'>Source</Text>
      <Text weight = 'bold' truncate>{sourceColony.getName()}</Text>
    </Title>
  );

  return (
    <Card>

      <Inset side = 'top' pb = 'current'>
        <Header>
          {candidates.length <= 1 ? title : <Picker trigger = {title} options = {options} value = {sourceColony.cass.id} onChange = {onChange} align = 'start' />}

          {candidates.length > 1 && (
            <>
              <Arrow variant = 'ghost' color = 'gray' onClick = {onPrev} aria-label = 'Previous source colony'>
                <ChevronLeftIcon />
              </Arrow>
              <Arrow variant = 'ghost' color = 'gray' onClick = {onNext} aria-label = 'Next source colony'>
                <ChevronRightIcon />
              </Arrow>
            </>
          )}
        </Header>
      </Inset>

      <Flex justify = 'between' align = 'baseline'>
        <Text.Note>Influence</Text.Note>
        <ColonyInfluence colony = {sourceColony} influenceDelta = {influenceDelta} />
      </Flex>

    </Card>
  );
};
